import { FormEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { useI18n } from '../i18n';

// Modal dialog on the native <dialog> element: showModal() makes the rest of
// the page inert, traps focus and closes on Escape. Focus returns to whatever
// opened it. Destructive actions are never the default button (§10): the
// form has no implicit submit target other than the primary button, and the
// primary button of a destructive dialog is styled as danger, not filled.
//
// Layout: title, an optional "who sees this" line, the body, then Cancel and
// the action (ghost first, primary second, as everywhere).
export function Dialog({
  open,
  title,
  intro,
  children,
  submitLabel,
  busyLabel,
  busy = false,
  danger = false,
  submitDisabled = false,
  error,
  onSubmit,
  onClose,
  wide = false,
  hideSubmit = false,
  closeLabel,
}: {
  open: boolean;
  title: string;
  intro?: ReactNode;
  children?: ReactNode;
  submitLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  danger?: boolean;
  submitDisabled?: boolean;
  error?: string | null;
  onSubmit?: () => void;
  onClose: () => void;
  wide?: boolean;
  hideSubmit?: boolean;
  closeLabel?: string;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const errId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      d.showModal();
    } else if (!open && d.open) {
      d.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!busy && !submitDisabled) onSubmit?.();
  }

  return (
    <dialog
      ref={ref}
      className={`dialog panel${wide ? ' dialog-wide' : ''}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
    >
      {open && (
        <form className="dialog-form" onSubmit={submit} noValidate>
          <h2 className="panel-title" id={titleId}>
            {/* Titles name the task; the "+" belongs only on the button that opens it. */}
            {title.replace(/^\+\s*/, '')}
          </h2>
          {intro && <div className="dialog-intro">{intro}</div>}
          {children}
          {error && (
            <p className="error-text" id={errId} role="alert">
              {error}
            </p>
          )}
          <div className="form-actions dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              {closeLabel ?? t['form.cancel']}
            </button>
            {!hideSubmit && (
              <button
                type="submit"
                className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
                disabled={busy || submitDisabled}
                aria-describedby={error ? errId : undefined}
              >
                {busy ? (busyLabel ?? t['pa.working']) : submitLabel}
              </button>
            )}
          </div>
        </form>
      )}
    </dialog>
  );
}

/** A labelled field with an optional visible hint, wired with aria-describedby. */
export function Field({
  label,
  hint,
  children,
  id,
}: {
  label: string;
  hint?: ReactNode;
  id: string;
  children: ReactNode;
}) {
  return (
    <div className="field-group">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && (
        <p className="form-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
    </div>
  );
}
